/**
 * ABR filter form dialog — multi-step stepper dialog for create and edit flows.
 *
 * When source = Classified content (reportedContent = false):
 *   Step 1: Configure — name, description, source toggle
 *   Step 2: Text Labels — text label filters with severity thresholds + AND/OR modifier
 *   Step 3: Image Labels — image label filters with score thresholds + AND/OR modifier
 *   Step 4: Review & Submit
 *
 * When source = Reported content (reportedContent = true):
 *   Step 1: Configure — name, description, source toggle
 *   Step 2: Review & Submit (label steps hidden)
 *
 * Uses primaryAction/secondaryAction on Dialog for footer buttons (same as CreatePolicyDialog).
 * Displays ErrorBanner for mutation errors (same as ModerationLabelDialog).
 */

import { ErrorBanner } from '@core/components';
import type { AbrFilter, LabelFilterInput } from '@moderation/domain/AbrFilter';
import { FilterOperator } from '@moderation/domain/AbrFilter';
import { useCreateAbrFilter, useUpdateAbrFilter } from '@moderation/hooks';
import { Dialog, type DialogAction, StepperDots, type StepConfig } from '@soteria/design-system-ve';
import { useStandardLabels } from '@system-config/label-management/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';

import * as styles from './AbrFilterFormDialog.css';
import { ConfigureStep } from './ConfigureStep';
import { ImageLabelFilterStep } from './ImageLabelFilterStep';
import { ReviewStep } from './ReviewStep';
import { TextLabelFilterStep } from './TextLabelFilterStep';
import { isFilterOperator } from './abrFilterConstants';

// ---------------------------------------------------------------------------
// Step definitions
// ---------------------------------------------------------------------------

const STEPS_WITH_LABELS: StepConfig[] = [
  { id: 'configure', label: 'Configure', description: 'Set filter name and source' },
  { id: 'text-labels', label: 'Text Labels', description: 'Add text label filters' },
  { id: 'image-labels', label: 'Image Labels', description: 'Add image label filters' },
  { id: 'review', label: 'Review & Save', description: 'Confirm settings' },
];

const STEPS_SOURCE_ONLY: StepConfig[] = [
  { id: 'configure', label: 'Configure', description: 'Set filter name and source' },
  { id: 'review', label: 'Review & Save', description: 'Confirm settings' },
];

// Step IDs for each flow
const FLOW_WITH_LABELS = ['configure', 'text-labels', 'image-labels', 'review'] as const;
const FLOW_SOURCE_ONLY = ['configure', 'review'] as const;

// ---------------------------------------------------------------------------
// Main dialog
// ---------------------------------------------------------------------------

interface AbrFilterFormDialogProps {
  communityId: string;
  filter: AbrFilter | null;
  isOpen: boolean;
  onClose: () => void;
}

// eslint-disable-next-line complexity, max-statements -- dialog submit/navigation branching is clearer inline than split into multiple handlers
export function AbrFilterFormDialog({ communityId, filter, isOpen, onClose }: AbrFilterFormDialogProps) {
  const isEditMode = filter !== null;
  const { createFilter, loading: creating, errors: createErrors, resetErrors: resetCreateErrors } = useCreateAbrFilter(communityId);
  const { updateFilter, loading: updating, errors: updateErrors, resetErrors: resetUpdateErrors } = useUpdateAbrFilter(communityId);
  const { labels: standardLabels, loading: labelsLoading, errors: labelErrors } = useStandardLabels();

  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [reportedContent, setReportedContent] = useState(false);
  const [textLabelFilters, setTextLabelFilters] = useState<LabelFilterInput[]>([]);
  const [textLabelFilterModifier, setTextLabelFilterModifier] = useState<FilterOperator>(FilterOperator.And);
  const [imageLabelFilters, setImageLabelFilters] = useState<LabelFilterInput[]>([]);
  const [imageLabelFilterModifier, setImageLabelFilterModifier] = useState<FilterOperator>(FilterOperator.And);

  // Label steps only shown for classified content
  const showLabelSteps = !reportedContent;
  const steps = showLabelSteps ? STEPS_WITH_LABELS : STEPS_SOURCE_ONLY;
  const flow = showLabelSteps ? FLOW_WITH_LABELS : FLOW_SOURCE_ONLY;
  const lastStep = steps.length - 1;
  const currentStepId = flow[step] ?? flow[0];

  // Combine errors from create, update, and label loading hooks
  const errors = useMemo(() => createErrors ?? updateErrors ?? labelErrors ?? null, [createErrors, updateErrors, labelErrors]);
  const errorTitle = createErrors || updateErrors ? 'There was an error saving the filter:' : 'There was an error loading labels:';

  useEffect(() => {
    if (filter) {
      setName(filter.name);
      setDescription(filter.description || '');
      setReportedContent(filter.criteria.reportedContent);
      setTextLabelFilters(
        (filter.criteria.textLabelFilters ?? []).map(f => ({
          labelId: f.labelId,
          minSeverity: f.minSeverity ?? undefined,
        }))
      );
      setTextLabelFilterModifier(isFilterOperator(filter.criteria.textLabelFilterModifier) ? filter.criteria.textLabelFilterModifier : FilterOperator.And);
      setImageLabelFilters(
        (filter.criteria.imageLabelFilters ?? []).map(f => ({
          labelId: f.labelId,
          minScore: f.minScore ?? undefined,
        }))
      );
      setImageLabelFilterModifier(isFilterOperator(filter.criteria.imageLabelFilterModifier) ? filter.criteria.imageLabelFilterModifier : FilterOperator.And);
    } else {
      setName('');
      setDescription('');
      setReportedContent(false);
      setTextLabelFilters([]);
      setTextLabelFilterModifier(FilterOperator.And);
      setImageLabelFilters([]);
      setImageLabelFilterModifier(FilterOperator.And);
    }

    setStep(0);
  }, [filter, isOpen]);

  const handleToggleSource = useCallback(() => {
    setReportedContent(prev => {
      const next = !prev;

      // Clear label filters when switching to reported content
      if (next) {
        setTextLabelFilters([]);
        setTextLabelFilterModifier(FilterOperator.And);
        setImageLabelFilters([]);
        setImageLabelFilterModifier(FilterOperator.And);
      }

      return next;
    });

    // Reset to step 0 when toggling source to avoid being on a non-existent step
    setStep(0);
  }, []);

  const handleSubmit = useCallback(async () => {
    const trimmedName = name.trim();
    const trimmedDescription = description.trim() || undefined;
    const criteria = {
      reportedContent,
      textLabelFilters: textLabelFilters.length > 0 ? textLabelFilters : undefined,
      textLabelFilterModifier: textLabelFilters.length > 0 ? textLabelFilterModifier : undefined,
      imageLabelFilters: imageLabelFilters.length > 0 ? imageLabelFilters : undefined,
      imageLabelFilterModifier: imageLabelFilters.length > 0 ? imageLabelFilterModifier : undefined,
    };

    let success = false;

    if (isEditMode && filter) {
      const result = await updateFilter({
        filterId: filter.id,
        name: trimmedName,
        description: trimmedDescription,
        criteria,
      });

      success = result !== null;
    } else {
      const result = await createFilter({
        name: trimmedName,
        description: trimmedDescription,
        criteria,
      });

      success = result !== null;
    }

    if (success) {
      onClose();
    }
  }, [
    isEditMode,
    filter,
    name,
    description,
    reportedContent,
    textLabelFilters,
    textLabelFilterModifier,
    imageLabelFilters,
    imageLabelFilterModifier,
    createFilter,
    updateFilter,
    onClose,
  ]);

  const handleClose = useCallback(() => {
    resetCreateErrors();
    resetUpdateErrors();
    onClose();
  }, [resetCreateErrors, resetUpdateErrors, onClose]);

  const isBusy = creating || updating;
  const title = isEditMode ? 'Edit Queue Filter' : 'Create Queue Filter';
  const canProceed = name.trim().length > 0;
  const isFirstStep = step === 0;
  const isLastStep = step === lastStep;

  // Footer buttons
  const primaryAction: DialogAction = {
    label: isFirstStep ? 'Cancel' : 'Back',
    onClick: isFirstStep ? handleClose : () => setStep(step - 1),
    disabled: isBusy,
    variant: 'secondary',
  };

  const submitLabel = isBusy ? 'Saving…' : isEditMode ? 'Save Filter' : 'Create Filter';

  const secondaryAction: DialogAction = {
    label: isLastStep ? submitLabel : 'Next',
    onClick: isLastStep ? handleSubmit : () => setStep(step + 1),
    disabled: isLastStep ? isBusy : !canProceed,
    variant: 'primary',
  };

  return (
    <Dialog
      customHeader={
        <div className={styles.dialogHeaderStyles}>
          <h2 className={styles.dialogTitleStyles}>{title}</h2>
          <StepperDots
            interactive
            showLabels
            activeStep={step}
            size="medium"
            steps={steps}
            onStepClick={index => {
              if (index <= step || canProceed) setStep(index);
            }}
          />
        </div>
      }
      isOpen={isOpen}
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
      size="xxl"
      onClose={handleClose}
    >
      <div className={styles.dialogContentStyles}>
        {errors && errors.length > 0 && <ErrorBanner errors={errors} title={errorTitle} />}

        {currentStepId === 'configure' && (
          <ConfigureStep
            description={description}
            name={name}
            reportedContent={reportedContent}
            onDescriptionChange={setDescription}
            onNameChange={setName}
            onToggleSource={handleToggleSource}
          />
        )}

        {currentStepId === 'text-labels' && (
          <TextLabelFilterStep
            labelsLoading={labelsLoading}
            standardLabels={standardLabels}
            textLabelFilterModifier={textLabelFilterModifier}
            textLabelFilters={textLabelFilters}
            onTextLabelFilterModifierChange={setTextLabelFilterModifier}
            onTextLabelFiltersChange={setTextLabelFilters}
          />
        )}

        {currentStepId === 'image-labels' && (
          <ImageLabelFilterStep
            imageLabelFilterModifier={imageLabelFilterModifier}
            imageLabelFilters={imageLabelFilters}
            labelsLoading={labelsLoading}
            standardLabels={standardLabels}
            onImageLabelFilterModifierChange={setImageLabelFilterModifier}
            onImageLabelFiltersChange={setImageLabelFilters}
          />
        )}

        {currentStepId === 'review' && (
          <ReviewStep
            description={description}
            imageLabelFilterModifier={imageLabelFilterModifier}
            imageLabelFilters={imageLabelFilters}
            name={name}
            reportedContent={reportedContent}
            standardLabels={standardLabels}
            textLabelFilterModifier={textLabelFilterModifier}
            textLabelFilters={textLabelFilters}
          />
        )}
      </div>
    </Dialog>
  );
}
